import React, { useEffect, useState, useCallback } from "react";
import { Table, Spinner, Button, Form, Row, Col, Badge, Alert as BsAlert, Card, Nav, Tab } from "react-bootstrap";
import SafetyDpiService from "../../../services/safety/SafetyDpiService";
import LavoratoreSearchPicker from "../LavoratoreSearchPicker/LavoratoreSearchPicker";

const EMPTY_CONSEGNA = {
    id_lavoratore: "",
    id_tipo_dpi: "",
    data_consegna: "",
    quantita: "1",
    taglia: "",
    matricola_seriale: "",
    data_scadenza_sostituzione: "",
    note: "",
};

function apiErr(err, fallback) {
    if (!err) return fallback;
    if (typeof err === "string") return err;
    const m = err.message || err.messaggio || err.error?.message || err.error;
    if (typeof m === "string" && m.trim()) return m;
    if (m && typeof m === "object" && m.message) return m.message;
    return fallback;
}

function fmtDate(d) {
    if (!d) return "—";
    const s = String(d).substring(0, 10);
    const p = s.split("-");
    if (p.length !== 3) return s;
    return `${p[2]}/${p[1]}/${p[0]}`;
}

function groupByCategoria(tipi) {
    const groups = {};
    tipi.forEach(t => {
        const cat = t.categoria || "Altri";
        if (!groups[cat]) groups[cat] = [];
        groups[cat].push(t);
    });
    return Object.entries(groups);
}

function statoBadge(stato, giorni) {
    const map = {
        SCADUTO:      { bg: "danger",  text: "light", label: "Scaduto" },
        IN_SCADENZA:  { bg: "warning", text: "dark",  label: "In scadenza" },
        VALIDO:       { bg: "success", text: "light", label: "Valido" },
        PERMANENTE:   { bg: "light",   text: "dark",  label: "Senza scadenza" },
    };
    const m = map[stato] || map.PERMANENTE;
    return (
        <span>
            <Badge bg={m.bg} text={m.text} className={m.bg === "light" ? "border" : undefined}>{m.label}</Badge>
            {typeof giorni === "number" && (stato === "SCADUTO" || stato === "IN_SCADENZA") && (
                <span className="text-muted small ms-1">
                    ({giorni < 0 ? `${Math.abs(giorni)} gg fa` : `tra ${giorni} gg`})
                </span>
            )}
        </span>
    );
}

/**
 * DpiPanel — matrice DPI per mansione e registro consegne.
 *
 * Props:
 *  - idAzienda: number (richiesto)
 *  - settore: string opzionale
 */
export default function DpiPanel({ idAzienda, settore }) {
    const [mansioni, setMansioni] = useState([]);
    const [tipi, setTipi] = useState([]);
    const [matrice, setMatrice] = useState([]);
    const [consegne, setConsegne] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [msg, setMsg] = useState(null);

    const [selMansione, setSelMansione] = useState("");
    const [selTipi, setSelTipi] = useState([]);
    const [savingMatrice, setSavingMatrice] = useState(false);

    const [showConsegna, setShowConsegna] = useState(false);
    const [form, setForm] = useState(EMPTY_CONSEGNA);
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);

    const parseJson = async (resp) => {
        if (resp && typeof resp.json === "function") return resp.json();
        return resp;
    };

    const loadStats = useCallback(async () => {
        if (!idAzienda) return;
        try {
            const resp = await SafetyDpiService.stats(idAzienda);
            const data = await parseJson(resp);
            setStats(data && !data.message ? data : null);
        } catch {
            setStats(null);
        }
    }, [idAzienda]);

    const loadMatrice = useCallback(async () => {
        if (!idAzienda) {
            setMansioni([]);
            setTipi([]);
            setMatrice([]);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const resp = await SafetyDpiService.matriceByAzienda(idAzienda);
            const data = await parseJson(resp);
            if (data?.message || data?.error) {
                throw new Error(data.message || data.error);
            }
            setMansioni(Array.isArray(data?.mansioni) ? data.mansioni : []);
            setTipi(Array.isArray(data?.tipi) ? data.tipi : []);
            setMatrice(Array.isArray(data?.matrice) ? data.matrice : []);
        } catch (err) {
            const m = apiErr(err, "");
            setError(
                m.includes("NetworkError") || m.includes("Failed to fetch")
                    ? "API non raggiungibile. Verifica deploy: SafetyDpiController + Service, auth.json, SQL 2026-06-15."
                    : (m || "Errore caricamento matrice DPI. Controlla SQL e deploy backend.")
            );
        } finally {
            setLoading(false);
        }
    }, [idAzienda]);

    const loadConsegne = useCallback(async () => {
        if (!idAzienda) { setConsegne([]); return; }
        try {
            const resp = await SafetyDpiService.consegneByAzienda(idAzienda);
            const data = await parseJson(resp);
            setConsegne(Array.isArray(data) ? data : []);
        } catch (err) {
            setConsegne([]);
            setError(apiErr(err, "Errore caricamento registro consegne DPI."));
        }
    }, [idAzienda]);

    const reloadAll = useCallback(() => {
        loadStats();
        loadMatrice();
        loadConsegne();
    }, [loadStats, loadMatrice, loadConsegne]);

    useEffect(() => { reloadAll(); }, [reloadAll]);

    useEffect(() => {
        if (!selMansione) {
            setSelTipi([]);
            return;
        }
        const idM = parseInt(selMansione, 10);
        const ids = matrice
            .filter(r => parseInt(r.id_mansione, 10) === idM)
            .map(r => parseInt(r.id_tipo_dpi, 10));
        setSelTipi(ids);
    }, [selMansione, matrice]);

    const toggleTipo = (idTipo) => {
        const id = parseInt(idTipo, 10);
        setSelTipi(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
    };

    const handleSaveMatrice = async () => {
        if (!idAzienda || !selMansione) {
            setError("Seleziona una mansione.");
            return;
        }
        setSavingMatrice(true);
        setError(null);
        setMsg(null);
        try {
            const resp = await SafetyDpiService.saveMatrice(idAzienda, parseInt(selMansione, 10), selTipi);
            const data = await parseJson(resp);
            if (data?.message && !data?.success) throw new Error(data.message);
            setMsg(`Matrice salvata (${selTipi.length} DPI per la mansione selezionata).`);
            await loadMatrice();
            await loadStats();
        } catch (err) {
            setError(apiErr(err, "Errore salvataggio matrice DPI."));
        } finally {
            setSavingMatrice(false);
        }
    };

    const handleSaveConsegna = async (e) => {
        e.preventDefault();
        if (!form.id_lavoratore || !form.id_tipo_dpi || !form.data_consegna) {
            setError("Compila lavoratore, tipo DPI e data consegna.");
            return;
        }
        setSaving(true);
        setError(null);
        setMsg(null);
        try {
            const fd = new FormData();
            fd.append("id_azienda", idAzienda);
            fd.append("id_lavoratore", form.id_lavoratore);
            fd.append("id_tipo_dpi", form.id_tipo_dpi);
            fd.append("data_consegna", form.data_consegna);
            fd.append("quantita", form.quantita || "1");
            if (form.taglia) fd.append("taglia", form.taglia);
            if (form.matricola_seriale) fd.append("matricola_seriale", form.matricola_seriale);
            if (form.data_scadenza_sostituzione) fd.append("data_scadenza_sostituzione", form.data_scadenza_sostituzione);
            if (form.note) fd.append("note", form.note);
            if (file) fd.append("file", file);
            const resp = await SafetyDpiService.createConsegna(fd);
            const data = await parseJson(resp);
            if (data?.message && !data?.success) throw new Error(data.message);
            setMsg("Consegna DPI registrata.");
            setShowConsegna(false);
            setForm(EMPTY_CONSEGNA);
            setFile(null);
            await loadConsegne();
            await loadStats();
        } catch (err) {
            setError(apiErr(err, "Errore registrazione consegna DPI."));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Eliminare questa consegna DPI?")) return;
        setError(null);
        try {
            await SafetyDpiService.deleteConsegna(id);
            setMsg("Consegna eliminata.");
            await loadConsegne();
            await loadStats();
        } catch (err) {
            setError(apiErr(err, "Errore eliminazione consegna."));
        }
    };

    const handleDownload = async (c) => {
        try {
            await SafetyDpiService.download(c.id_consegna, `consegna_dpi_${c.id_consegna}.pdf`);
        } catch (err) {
            setError(apiErr(err, "Errore download allegato."));
        }
    };

    if (!idAzienda) {
        return (
            <BsAlert variant="info" className="py-2 mb-0">
                <i className="bi bi-info-circle me-1" />
                Salva prima i dati dell&apos;azienda per gestire DPI per mansione.
            </BsAlert>
        );
    }

    const mansioneSel = mansioni.find(m => String(m.id_mansione) === String(selMansione));

    return (
        <div>
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                <div>
                    <h5 className="mb-1">
                        <i className="bi bi-shield me-2 text-primary" />
                        DPI per mansione
                    </h5>
                    <p className="text-muted small mb-0">
                        Matrice DPI obbligatori per mansione e registro consegne con ricevuta/firma.
                    </p>
                </div>
                <Button variant="outline-primary" size="sm" onClick={reloadAll} disabled={loading}>
                    <i className="bi bi-arrow-clockwise me-1" />Aggiorna
                </Button>
            </div>

            {error && <BsAlert variant="danger" className="py-2" onClose={() => setError(null)} dismissible>{error}</BsAlert>}
            {msg && <BsAlert variant="success" className="py-2" onClose={() => setMsg(null)} dismissible>{msg}</BsAlert>}

            {stats && (
                <Row className="g-2 mb-3">
                    <Col xs={6} md={4} lg={2}>
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Body className="py-2 px-3">
                                <div className="text-muted small">Mansioni config.</div>
                                <div className="fw-bold fs-5">{stats.matrice_mansioni ?? 0}</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={6} md={4} lg={2}>
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Body className="py-2 px-3">
                                <div className="text-muted small">Consegne anno</div>
                                <div className="fw-bold fs-5">{stats.consegne_anno ?? 0}</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={6} md={4} lg={2}>
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Body className="py-2 px-3">
                                <div className="text-muted small">DPI scaduti</div>
                                <div className={`fw-bold fs-5 ${(stats.consegne_scadute ?? 0) > 0 ? "text-danger" : ""}`}>{stats.consegne_scadute ?? 0}</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={6} md={4} lg={2}>
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Body className="py-2 px-3">
                                <div className="text-muted small">In scadenza</div>
                                <div className={`fw-bold fs-5 ${(stats.consegne_in_scadenza ?? 0) > 0 ? "text-warning" : ""}`}>{stats.consegne_in_scadenza ?? 0}</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={6} md={4} lg={2}>
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Body className="py-2 px-3">
                                <div className="text-muted small">Lav. non conformi</div>
                                <div className={`fw-bold fs-5 ${(stats.lavoratori_non_conformi ?? 0) > 0 ? "text-danger" : "text-success"}`}>{stats.lavoratori_non_conformi ?? 0}</div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            <Tab.Container defaultActiveKey="matrice">
                <Nav variant="tabs" className="mb-3">
                    <Nav.Item><Nav.Link eventKey="matrice">Matrice DPI</Nav.Link></Nav.Item>
                    <Nav.Item><Nav.Link eventKey="consegne">Registro consegne</Nav.Link></Nav.Item>
                </Nav>
                <Tab.Content>
                    <Tab.Pane eventKey="matrice">
                        {loading ? (
                            <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
                        ) : mansioni.length === 0 ? (
                            <BsAlert variant="info" className="py-2">
                                Nessuna mansione trovata. Assegna una mansione ai lavoratori attivi dell&apos;azienda per configurare la matrice DPI.
                            </BsAlert>
                        ) : (
                            <>
                                <Row className="g-2 mb-3 align-items-end">
                                    <Col md={6}>
                                        <Form.Label className="small text-muted mb-1">Mansione</Form.Label>
                                        <Form.Select value={selMansione} onChange={e => setSelMansione(e.target.value)}>
                                            <option value="">— Seleziona mansione —</option>
                                            {mansioni.map(m => (
                                                <option key={m.id_mansione} value={m.id_mansione}>
                                                    {m.denominazione} ({m.lavoratori_count ?? 0} lav.)
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Col>
                                    {mansioneSel && (
                                        <Col md="auto">
                                            <Button variant="primary" size="sm" onClick={handleSaveMatrice} disabled={savingMatrice}>
                                                {savingMatrice ? <Spinner animation="border" size="sm" /> : <><i className="bi bi-check-lg me-1" />Salva matrice</>}
                                            </Button>
                                        </Col>
                                    )}
                                </Row>
                                {selMansione && tipi.length > 0 ? (
                                    groupByCategoria(tipi).map(([cat, items]) => (
                                        <Card key={cat} className="mb-2 border-0 shadow-sm">
                                            <Card.Header className="py-2 bg-light small fw-semibold">{cat}</Card.Header>
                                            <Card.Body className="py-2">
                                                <Row>
                                                    {items.map(t => (
                                                        <Col key={t.id_tipo_dpi} xs={12} md={6} lg={4} className="mb-1">
                                                            <Form.Check
                                                                type="checkbox"
                                                                id={`dpi-${t.id_tipo_dpi}`}
                                                                label={
                                                                    <span>
                                                                        {t.denominazione}
                                                                        {t.riferimento_normativo && (
                                                                            <span className="text-muted small d-block">{t.riferimento_normativo}</span>
                                                                        )}
                                                                    </span>
                                                                }
                                                                checked={selTipi.includes(parseInt(t.id_tipo_dpi, 10))}
                                                                onChange={() => toggleTipo(t.id_tipo_dpi)}
                                                            />
                                                        </Col>
                                                    ))}
                                                </Row>
                                            </Card.Body>
                                        </Card>
                                    ))
                                ) : selMansione ? (
                                    <BsAlert variant="warning" className="py-2">Catalogo DPI non disponibile per il settore aziendale.</BsAlert>
                                ) : null}
                            </>
                        )}
                    </Tab.Pane>

                    <Tab.Pane eventKey="consegne">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="text-muted small">{consegne.length} consegne registrate</span>
                            <Button variant="success" size="sm" onClick={() => { setShowConsegna(!showConsegna); setForm({ ...EMPTY_CONSEGNA, data_consegna: new Date().toISOString().substring(0, 10) }); }}>
                                <i className="bi bi-plus-lg me-1" />Nuova consegna
                            </Button>
                        </div>

                        {showConsegna && (
                            <Card className="mb-3 border-primary">
                                <Card.Body>
                                    <Form onSubmit={handleSaveConsegna}>
                                        <Row className="g-2">
                                            <Col md={6}>
                                                <Form.Label className="small">Lavoratore *</Form.Label>
                                                <LavoratoreSearchPicker
                                                    idAzienda={idAzienda}
                                                    value={form.id_lavoratore}
                                                    onChange={id => setForm(f => ({ ...f, id_lavoratore: id }))}
                                                />
                                            </Col>
                                            <Col md={6}>
                                                <Form.Label className="small">Tipo DPI *</Form.Label>
                                                <Form.Select value={form.id_tipo_dpi} onChange={e => setForm(f => ({ ...f, id_tipo_dpi: e.target.value }))} required>
                                                    <option value="">— Seleziona —</option>
                                                    {tipi.map(t => (
                                                        <option key={t.id_tipo_dpi} value={t.id_tipo_dpi}>{t.denominazione}</option>
                                                    ))}
                                                </Form.Select>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Label className="small">Data consegna *</Form.Label>
                                                <Form.Control type="date" value={form.data_consegna} onChange={e => setForm(f => ({ ...f, data_consegna: e.target.value }))} required />
                                            </Col>
                                            <Col md={2}>
                                                <Form.Label className="small">Quantità</Form.Label>
                                                <Form.Control type="number" min="1" value={form.quantita} onChange={e => setForm(f => ({ ...f, quantita: e.target.value }))} />
                                            </Col>
                                            <Col md={2}>
                                                <Form.Label className="small">Taglia</Form.Label>
                                                <Form.Control value={form.taglia} onChange={e => setForm(f => ({ ...f, taglia: e.target.value }))} />
                                            </Col>
                                            <Col md={3}>
                                                <Form.Label className="small">Matricola / seriale</Form.Label>
                                                <Form.Control value={form.matricola_seriale} onChange={e => setForm(f => ({ ...f, matricola_seriale: e.target.value }))} />
                                            </Col>
                                            <Col md={3}>
                                                <Form.Label className="small">Scadenza sostituzione</Form.Label>
                                                <Form.Control type="date" value={form.data_scadenza_sostituzione} onChange={e => setForm(f => ({ ...f, data_scadenza_sostituzione: e.target.value }))} />
                                            </Col>
                                            <Col md={6}>
                                                <Form.Label className="small">Allegato (ricevuta/firma)</Form.Label>
                                                <Form.Control type="file" onChange={e => setFile(e.target.files?.[0] || null)} />
                                            </Col>
                                            <Col md={12}>
                                                <Form.Label className="small">Note</Form.Label>
                                                <Form.Control as="textarea" rows={2} value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
                                            </Col>
                                        </Row>
                                        <div className="mt-2">
                                            <Button type="submit" variant="primary" size="sm" disabled={saving} className="me-2">
                                                {saving ? <Spinner animation="border" size="sm" /> : "Registra consegna"}
                                            </Button>
                                            <Button variant="outline-secondary" size="sm" onClick={() => setShowConsegna(false)}>Annulla</Button>
                                        </div>
                                    </Form>
                                </Card.Body>
                            </Card>
                        )}

                        {consegne.length === 0 ? (
                            <BsAlert variant="info" className="py-2">Nessuna consegna DPI registrata.</BsAlert>
                        ) : (
                            <div className="table-responsive">
                                <Table hover size="sm" className="align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Data</th>
                                            <th>Lavoratore</th>
                                            <th>DPI</th>
                                            <th>Taglia / matricola</th>
                                            <th>Scadenza</th>
                                            <th>Stato</th>
                                            <th className="text-end">Azioni</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {consegne.map(c => (
                                            <tr key={c.id_consegna}>
                                                <td>{fmtDate(c.data_consegna)}</td>
                                                <td>
                                                    <div>{c.lav_cognome} {c.lav_nome}</div>
                                                    {c.mansione_denominazione && <span className="text-muted small">{c.mansione_denominazione}</span>}
                                                </td>
                                                <td>
                                                    <div>{c.tipo_denominazione || "—"}</div>
                                                    {c.tipo_categoria && <span className="text-muted small">{c.tipo_categoria}</span>}
                                                </td>
                                                <td className="small">
                                                    {c.taglia && <div>Taglia: {c.taglia}</div>}
                                                    {c.matricola_seriale && <div>S/N: {c.matricola_seriale}</div>}
                                                    {!c.taglia && !c.matricola_seriale && "—"}
                                                </td>
                                                <td>{fmtDate(c.data_scadenza_sostituzione)}</td>
                                                <td>{statoBadge(c.stato_scadenza, c.giorni_alla_scadenza)}</td>
                                                <td className="text-end text-nowrap">
                                                    {c.allegato_presente && (
                                                        <Button variant="outline-secondary" size="sm" className="me-1" onClick={() => handleDownload(c)} title="Scarica allegato">
                                                            <i className="bi bi-download" />
                                                        </Button>
                                                    )}
                                                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(c.id_consegna)} title="Elimina">
                                                        <i className="bi bi-trash" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>
        </div>
    );
}
